import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ScanSearch, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad } from "@/lib/api";
import { gbp } from "@/lib/format";

const Change = ({ c }) => (
  <li className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-t border-slate-100 py-2 text-sm" data-testid={`refcheck-change-${c.doc_id}`}>
    <span className="font-semibold">{c.supplier}</span><span className="text-xs text-slate-500">{c.date} · {gbp(c.amount)}</span>
    <span className="font-mono text-xs">{c.old}{c.new ? <> → <b className="text-teal-700">{c.new}</b></> : ""}</span>
    <span className="w-full text-xs text-slate-500">{c.note}</span>
  </li>
);

export const RefCheck = ({ onDone }) => {
  const api = useApi();
  const st = useLoad("/docs/reread-refs");
  const [busy, setBusy] = useState(false);
  const job = st.data?.job;
  const running = job?.status === "running";
  useEffect(() => {
    if (!running) return;
    const t = setInterval(st.reload, 3000);
    return () => clearInterval(t);
  }, [running, st.reload]);
  useEffect(() => { if (job?.status === "done") onDone?.(); }, [job?.status, onDone]);
  if (!st.data || !st.data.enabled || (!job && !st.data.unchecked)) return null;
  const start = async () => {
    setBusy(true);
    try { await api("post", "/docs/reread-refs"); st.reload(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const changes = job?.changes || [];
  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="refcheck-panel">
      <div className="flex flex-wrap items-center gap-3">
        <ScanSearch className="h-5 w-5 text-teal-600" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">Check invoice numbers</p>
          <p className="text-xs text-slate-500" data-testid="refcheck-status">
            {running ? `Reading ${job.done} of ${job.total || "…"} saved invoices…`
              : job?.status === "done" ? `Checked ${job.total} invoice${job.total === 1 ? "" : "s"} · ${changes.filter((c) => c.new).length} corrected · ${changes.filter((c) => "new" in c && !c.new).length} delivery note only${st.data.unchecked ? ` · ${st.data.unchecked} new to check` : ""}`
              : `${st.data.unchecked} saved invoice${st.data.unchecked === 1 ? "" : "s"} may show a delivery note number instead of the invoice number.`}
          </p>
        </div>
        {running ? <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
          : st.data.unchecked > 0 ? <Button size="sm" onClick={start} disabled={busy} data-testid="refcheck-start-button">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Re-read and fix</Button>
          : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
      </div>
      {running && <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-teal-500 transition-[width]" style={{ width: `${job.total ? (100 * job.done) / job.total : 5}%` }} /></div>}
      {changes.length > 0 && <ul className="mt-3 max-h-72 overflow-y-auto" data-testid="refcheck-changes">{changes.map((c) => <Change key={c.doc_id} c={c} />)}</ul>}
    </div>
  );
};
